import PropTypes from "prop-types"
import { map, size } from "lodash"
import classnames from "classnames"
import { OfflineBolt } from "@mui/icons-material"
import { Select, MenuItem, Divider } from "@mui/material"
import Tooltip from "components/material/tooltip"
import Loading from "components/material/loading"
import { DROPDOWN_OPTION } from "helpers/shapes"
import { LOADING, OK } from "helpers/data-statuses"
import Error from "components/material/error"
import ModelChange from "./components/model-change"
import "./styles.scss"

export default function ModelSelector({
  status,
  modelId,
  modelName,
  sortedModelsByCoverage,
  setModel,
  resourceType,
  resourceId,
  disablePortal,
  disableUnderline,
  disabled,
  warningMessage,
  alternateLayout,
  modifiedScreens,
  openDialog,
  additionalOption,
}) {
  const onChange = (ev) => {
    const modelId = ev.target.value
    if (!modelId) {
      return
    }

    if (modelId === "email") {
      const mail = document.createElement("a")
      mail.href = "mailto:customersuccess@ompnt.com"
      mail.click()
    } else if (size(modifiedScreens) > 0) {
      openDialog({
        title: "Confirm Model Change",
        content: <ModelChange modifiedScreens={modifiedScreens} changeModel={() => setModel(modelId)} />,
      })
    } else {
      setModel(modelId)
    }
  }

  const requiresResourceCoverage = !!(resourceType && resourceId)
  const resourceTypeLabel = resourceType === "swap" ? "basket" : resourceType

  let innerComponent

  // Using the LoadingWrapper here prevented the MenuItem to trigger change when clicked
  switch (status) {
    case LOADING:
      innerComponent = <MenuItem className="portfolio-model-selection" value={modelId}>{modelName}</MenuItem>
      break
    case OK:
      innerComponent = map(sortedModelsByCoverage, (model, i) => {
        return (requiresResourceCoverage && !model.hasCoverage)
          ? <Tooltip
              key={i}
              title={`Model coverage for this ${resourceTypeLabel} is insufficient.`}
              classes={{ popper: "model-selector-coverage-info", tooltip: "coverage-insufficient-tooltip" }}
          >
            <div>
              <MenuItem key={i} className="portfolio-model-selection" disabled>{model.name}</MenuItem>
            </div>
          </Tooltip>
          : <MenuItem data-test-class="model-selector-item" key={i} className="portfolio-model-selection" value={model.id}>{model.name}</MenuItem>
      })
      break
    default:
      innerComponent = <Error text="There was an error while loading the models. Please try again." />
  }

  return <div className="modules_model-selector">
    {warningMessage && <Tooltip title={warningMessage} classes={{ tooltip: "warning-tooltip" }}>
      <OfflineBolt className={classnames("warning", { "alternate-layout": alternateLayout })} />
    </Tooltip>}
    <Select
      variant="standard"
      data-test-id="model-selector"
      className="model-selector"
      value={modelId}
      MenuProps={{ classes: { list: "model-selector-list" }, disablePortal }}
      SelectDisplayProps={{ id: "model-selector-display" }}
      onChange={onChange}
      disableUnderline={disableUnderline}
      disabled={disabled}
    >
      {additionalOption && <MenuItem value={additionalOption.value}>{additionalOption.label}</MenuItem>}
      {innerComponent}
      {status === LOADING && <Loading />}

      <Divider component="li" className="menu-selector-divider" />
      <MenuItem value="email">Want access to more models?&nbsp;<span className="more-model-link">Email us</span></MenuItem>
    </Select>
  </div>
}

ModelSelector.propTypes = {
  modelId: PropTypes.string.isRequired,
  modelName: PropTypes.string.isRequired,
  modifiedScreens: PropTypes.array,
  openDialog: PropTypes.func.isRequired,
  sortedModelsByCoverage: PropTypes.array.isRequired,
  noRestrictionModels: PropTypes.array,
  setModel: PropTypes.func.isRequired,
  status: PropTypes.string.isRequired,
  resourceType: PropTypes.string,
  resourceId: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  disablePortal: PropTypes.bool,
  disableUnderline: PropTypes.bool,
  disabled: PropTypes.bool,
  alternateLayout: PropTypes.bool,
  warningMessage: PropTypes.string,
  additionalOption: DROPDOWN_OPTION,
}
